'use es6';

import FormattedMessage from 'I18n/components/FormattedMessage';
import { hasSufficientEditPermission } from '../../../permissions/permission-logic';
import { useHasDemoDashboardWriteAccess } from '../../../access/scopes/hooks/useHasScope';
import { getDemo, getId, getUserPermissionLevel } from '../../../data/schemas/dashboardSchema';
import { getReportDashboardInfo } from '../../../data/schemas/reportSchema';
import useDashboardMaxWidgets from '../../../access/limits/hooks/useDashboardMaxWidgets';
import { DASHBOARD_PERMISSION_DENIED, IS_READ_ONLY_DEMO_DASHBOARD, NO_DASHBOARD_SELECTED, REPORT_ALREADY_ON_DASHBOARD, WILL_EXCEED_DASHBOARD_MAX_WIDGETS, ALREADY_OR_WILL_REACH_REPORT_LIMIT_IN_DASHBOARD } from '../../problems';
import { useDashboards } from '../../../data/hooks/useDashboards';
import { useAllDashboardReportCounts } from '../../../data/hooks/useAllDashboardReportCounts';
import { LOADING_PROBLEM_AND_MESSAGE } from '../dataProblemsAndMessages';
import { jsx as _jsx } from "react/jsx-runtime";
export const MAX_ALLOWED_REPORTS_IN_DASHBOARD = 30; // even pro/enterprise accounts can only have 30 reports in a dashboard

const usePlaceReportOnSpecificDashboardProblem = ({
  report,
  targetDashboardId,
  totalReportCountsToAdd = 1
}) => {
  const {
    data: dashboardsData,
    loading: dashboardsLoading
  } = useDashboards();
  const {
    data: dashboardWidgetCounts,
    loading: dashboardWidgetCountsLoading
  } = useAllDashboardReportCounts();
  const userCanWriteDemoDashboard = useHasDemoDashboardWriteAccess();
  const dashboardMaxWidgets = useDashboardMaxWidgets();
  if (dashboardsLoading || !dashboardsData || dashboardWidgetCountsLoading || !dashboardWidgetCounts) {
    return LOADING_PROBLEM_AND_MESSAGE;
  }
  if (!targetDashboardId) {
    return {
      problem: NO_DASHBOARD_SELECTED,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.placeReport.tooltip.noDashboardSelected"
      })
    };
  }
  const dashboards = dashboardsData.get('dashboards');
  const targetDashboard = dashboards.get(targetDashboardId);
  const targetDashboardWidgetCount = dashboardWidgetCounts.get(targetDashboardId);
  const targetIsReadOnlyDemo = getDemo(targetDashboard) && !userCanWriteDemoDashboard;
  const parentDashboardIds = report ? getReportDashboardInfo(report).map(parent => parent.get('id')) : [];
  const reportIsAlreadyOnTarget = parentDashboardIds.includes(getId(targetDashboard));
  const reachedDashboardMaxWidgets = targetDashboard && targetDashboardWidgetCount === dashboardMaxWidgets;
  const willExceedDashboardMaxWidgets = targetDashboard && targetDashboardWidgetCount + totalReportCountsToAdd > dashboardMaxWidgets;
  const shouldDisablePlaceReport = (reachedDashboardMaxWidgets || willExceedDashboardMaxWidgets) && dashboardMaxWidgets === MAX_ALLOWED_REPORTS_IN_DASHBOARD;
  const dashboardPermissionDenied = !hasSufficientEditPermission(getUserPermissionLevel(targetDashboard));
  if (dashboardPermissionDenied) {
    return {
      problem: DASHBOARD_PERMISSION_DENIED,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.placeReport.tooltip.dashboardPermissionDenied"
      })
    };
  }
  if (targetIsReadOnlyDemo) {
    return {
      problem: IS_READ_ONLY_DEMO_DASHBOARD,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.placeReport.tooltip.isReadOnlyDemo"
      })
    };
  }
  if (reportIsAlreadyOnTarget) {
    return {
      problem: REPORT_ALREADY_ON_DASHBOARD,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.placeReport.tooltip.reportAlreadyOnDashboard"
      })
    };
  }
  if (shouldDisablePlaceReport) {
    return {
      problem: ALREADY_OR_WILL_REACH_REPORT_LIMIT_IN_DASHBOARD,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.addReportToDashboard.banner.reachedLimit.description"
      })
    };
  }
  if (reachedDashboardMaxWidgets) {
    return {
      problem: WILL_EXCEED_DASHBOARD_MAX_WIDGETS,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.placeReport.tooltip.reachedMaxWidgets",
        options: {
          limit: dashboardMaxWidgets
        }
      })
    };
  }
  if (willExceedDashboardMaxWidgets) {
    return {
      problem: WILL_EXCEED_DASHBOARD_MAX_WIDGETS,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.placeReport.tooltip.willExceedMaxWidgets",
        options: {
          currentCount: targetDashboardWidgetCount,
          limit: dashboardMaxWidgets
        }
      })
    };
  }
  return {
    problem: null,
    disabledMessage: null
  };
};
export default usePlaceReportOnSpecificDashboardProblem;